import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { slice1Actions } from "../../store/reducer";
import { countNameInHtml, replaceNameInHtml } from "../../Common/placeRename";

// After a place is renamed (4.10.55, 2026-10-26): its events with exactly
// the old title were already renamed (PlaceEditModal); this asks what to do
// about the itinerary page when it still shows the old name — it's never
// changed by itself, since people edit it. Mounted once in App.js; opened by
// the "andiamo:place-renamed" event { oldName, newName, renamedEvents }, or
// { renames: [{ oldName, newName, renamedEvents }] } for several at once
// (Identify Places, 2026-10-28) — then one question covers them all.
// Photo Mosaic itineraries are rebuilt from the guide anyway, so no question.

export const PLACE_RENAMED_EVENT = "andiamo:place-renamed";

const RenameFollowUpModal = () => {
  const dispatch = useDispatch();
  const GuideFormat = useSelector((state) => state.GuideFormat);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    const onRenamed = (e) => {
      const d = e.detail || {};
      if (GuideFormat === "Photo Mosaic") return;
      const doc = window.guide_obj?.document || "";
      const renames = (d.renames || [d]).filter((r) => countNameInHtml(doc, r.oldName) > 0);
      const inItinerary = renames.reduce((n, r) => n + countNameInHtml(doc, r.oldName), 0);
      if (inItinerary > 0) setInfo({ ...d, renames, inItinerary, many: Boolean(d.renames) });
    };
    window.addEventListener(PLACE_RENAMED_EVENT, onRenamed);
    return () => window.removeEventListener(PLACE_RENAMED_EVENT, onRenamed);
  }, [GuideFormat]);

  if (!info) return null;
  const close = () => setInfo(null);
  const replace = () => {
    window.guide_obj.document = info.renames.reduce(
      (html, r) => replaceNameInHtml(html, r.oldName, r.newName).html,
      window.guide_obj.document
    );
    dispatch(slice1Actions.resetHomeScreen({ value: null }));
    close();
  };
  const rebuild = () => {
    close();
    if (window.openItineraryFromSchedule) window.openItineraryFromSchedule();
  };

  return (
    <Modal show onHide={close} centered style={{ zIndex: 70000 }}>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 18 }}>Update the itinerary?</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {info.many ? (
          <p style={{ marginBottom: 0 }}>
            The itinerary page still shows the old name{info.renames.length === 1 ? "" : "s"} of {info.renames.length} renamed place
            {info.renames.length === 1 ? "" : "s"} ({info.inItinerary === 1 ? "once" : `${info.inItinerary} times`}):{" "}
            {info.renames.map((r) => `"${r.oldName}" → "${r.newName}"`).join(", ")}.
          </p>
        ) : (
          <>
            <p>
              You renamed "{info.oldName}" to "{info.newName}".
              {info.renamedEvents > 0 ? ` ${info.renamedEvents} event${info.renamedEvents === 1 ? " was" : "s were"} renamed too.` : ""}
            </p>
            <p style={{ marginBottom: 0 }}>
              The itinerary page still shows the old name {info.inItinerary === 1 ? "once" : `${info.inItinerary} times`}.
            </p>
          </>
        )}
        <ul style={{ fontSize: 14, color: "#475569", marginTop: 8 }}>
          <li><b>Replace the name</b> — only the name{info.many && info.renames.length > 1 ? "s change" : " changes"}; everything else you wrote stays.</li>
          <li><b>Rebuild from the schedule</b> — makes a fresh itinerary; your own changes to it are lost.</li>
        </ul>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={close}>Not now</Button>
        <Button variant="outline-primary" onClick={rebuild}>Rebuild from the schedule</Button>
        <Button variant="primary" onClick={replace}>Replace the name</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RenameFollowUpModal;
